/**
 * 代行中の帯。
 *
 * 代行しているセッションでだけ出す(事業者の人が普通に開いたときの初期表示に
 * visumo 側の運用事情を持ち込まない)。出すのは 3 つ。
 *
 * - visumo が代行していること(誰かは役職名まで)
 * - **残り時間**(代行は 60 分で必ず切れる。切れる前に見えているようにする)
 * - 終了のボタン(終わらせたら、その場でログインへ戻る)
 *
 * ## 残り時間は 1 秒ごとに描き直す
 *
 * 分だけだと「残り 1 分」のまま 60 秒動かず、切れる直前ほど残りが読めない。
 * 終わる時刻そのものと、秒までの残りを並べて出す。
 *
 * 描き直すのは**この帯だけ**である。`now` はこの部品の state で、他の画面は
 * 巻き込まない(問い合わせも走らない)。
 *
 * ## 期限を過ぎたら、押す前に分かる形で終わっていると出す
 *
 * サーバー側は期限切れのトークンを断るので、押せば必ず落ちる。押してから
 * 断られるだけにしない。
 */

import { useEffect, useState } from "react";
import { useMutation } from "urql";
import { Button, useSession } from "visumo-data-agent-web-shared";

import { EndDelegationDocument } from "visumo-data-agent-web-shared";

export interface DelegationBannerProps {
  readonly actorLabel: string;
  readonly expiresAt: string;
}

/** 終わる時刻。**日本時間の hh:mm:ss**(この画面を見るのは日本の事業者)。 */
const CLOCK = new Intl.DateTimeFormat("ja-JP", {
  timeZone: "Asia/Tokyo",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/**
 * 残りの長さ。**分と秒で出す**(1 時間以上あるときだけ時も付ける)。
 *
 * 「残り 1 分未満」で止めると、切れる直前の 60 秒が読めない。
 */
function elapsedLabel(remainingMs: number): string {
  const total = Math.floor(remainingMs / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return hours === 0
    ? `${String(minutes)}:${pad(seconds)}`
    : `${String(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * 残り時間の文面。**推測で補わない**(読めない期限は「不明」)。
 *
 * 終わる時刻そのものを先に出す。残りだけだと、席を外して戻ったときに
 * 「いつ切れるのか」を数え直すことになる。
 */
export function remainingLabel(
  expiresAt: string,
  now: number,
): { readonly text: string; readonly expired: boolean } {
  const end = Date.parse(expiresAt);
  if (!Number.isFinite(end)) {
    return { text: "終了時刻は分かりません", expired: false };
  }
  const remainingMs = end - now;
  if (remainingMs <= 0) {
    return { text: "代行の時間が終わりました", expired: true };
  }
  return {
    text: `${CLOCK.format(end)} に終了します（残り ${elapsedLabel(remainingMs)}）`,
    expired: false,
  };
}

export function DelegationBanner(props: DelegationBannerProps) {
  const session = useSession();
  const [now, setNow] = useState(() => Date.now());
  const [result, endDelegation] = useMutation(EndDelegationDocument);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1_000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const remaining = remainingLabel(props.expiresAt, now);

  return (
    <div
      className="vd-notice vd-delegation-banner"
      role="status"
      data-testid="delegation-banner"
    >
      <span>
        <strong>visumo が代行中です</strong>（{props.actorLabel}）。
        {remaining.text}。
      </span>
      <Button
        variant="text"
        disabled={result.fetching}
        onClick={() => {
          void endDelegation({}).then((outcome) => {
            // 終わったら、そのセッションではもう何もできない。
            // 断られた場合も同じ(期限切れ・すでに終了)ので、どちらでも戻す
            if (outcome.error === undefined) {
              session.logout();
            }
          });
        }}
      >
        {remaining.expired ? "ログインへ戻る" : "代行を終了する"}
      </Button>
    </div>
  );
}
